import { FileDescriptor } from "@archipad-js/core/utils";
import { IllegalArgumentError } from "@archipad-js/core/error";

import { EntityContext } from "./entity-context";
import { getPropertyDefaultValue } from "./utils";
import { ORMError } from "./errors";
import { EntityState, EntityType } from "./types";

export abstract class BaseEntity {
    public readonly id!: string;

    public readonly entityType: EntityType;

    public readonly entityContext: EntityContext;

    public allowUndoRedo = true;

    private attachments: Record<string, FileDescriptor> = {};

    private state: EntityState = {
        data: {},
        updating: false,
    };

    public constructor(entityType: EntityType, entityContext: EntityContext) {
        this.entityType = entityType;
        this.entityContext = entityContext;

        // set defaults
        for (const key in this.entityType.metadata) {
            const prop = this.entityType.metadata[key];
            this.state.data[key] = getPropertyDefaultValue(prop);
        }
    }

    public getContext(): EntityContext {
        return this.entityContext;
    }

    public setAttachmentForKey(key: string, attachment: FileDescriptor): void {
        if (typeof key !== "string") {
            throw new IllegalArgumentError("Invalid key for setAttachmentForKey", "key");
        }

        key = key.normalize("NFD");
        const oldAttachment = this.attachments[key];
        if (oldAttachment != attachment) {
            if (attachment) {
                if (!this.entityContext) {
                    throw new ORMError(
                        "Attempting to set an attachment on a removed entity",
                        this.entityType.entityName,
                        this.id,
                    );
                }
                this.attachments[key] = attachment;
            } else {
                delete this.attachments[key];
            }
            if (this.entityContext) {
                this.entityContext.entityAttachmentUpdated(this, key, oldAttachment, attachment);
            }
        }
    }

    public getAttachmentForKey(key: string): FileDescriptor | undefined {
        key = key.normalize("NFD");
        return this.attachments[key];
    }

    public getAttachments(): BaseEntity["attachments"] {
        return this.attachments;
    }

    public hasAttachments(): boolean {
        for (const _key in this.attachments) {
            return true;
        }
        return false;
    }

    /**
     * Check if the entity has been persisted (if the user have synchronized its changes).
     */
    public isPersisted(): boolean {
        const context = this.entityContext;

        if (!context) {
            throw new ORMError("Entity is deleted", this.entityType.entityName, this.id);
        }

        return !context.createdEntities[this.entityType.entityName][this.id];
    }

    /**
     * Check if the entity has been deleted.
     */
    public isDeleted(): boolean {
        const context = this.entityContext;

        return !context;
    }
}

type BaseEntityInternal = typeof BaseEntity & {
    entityType: BaseEntity["entityType"];
    entityContext: BaseEntity["entityContext"];
    state: BaseEntity["state"];
};

export function toBaseEntityInternal(entity: BaseEntity): BaseEntityInternal {
    return entity as unknown as BaseEntityInternal;
}
